import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Empty,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
} from "antd";
import {
  DownOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { downloadRequest, getRequest } from "api";
import { getStoredWorkMonth } from "utils/workMonthStorage";
import { ClassLevelSelect } from "Components";
import ReportPrintPreviewModal, {
  SchoolReportResponse,
} from "./components/ReportPrintPreviewModal";
import "./Reports.css";

const { Option } = Select;

interface ReportMenuItem {
  reportType: string;
  name: string;
}

const REPORT_MENU_GROUPS: { groupName: string; items: ReportMenuItem[] }[] = [
  {
    groupName: "Tài chính - Công nợ",
    items: [
      {
        reportType: "CLASS_FINANCIAL_SUMMARY",
        name: "Tổng hợp Thu – Nợ theo Lớp",
      },
      {
        reportType: "INCOME_SOURCE_SUMMARY",
        name: "Doanh thu theo Nguồn thu",
      },
      {
        reportType: "STUDENT_DEBT_DETAIL",
        name: "Danh sách Học sinh Còn nợ",
      },
      {
        reportType: "CASHBOOK_TRANSACTIONS",
        name: "Sổ quỹ & Giao dịch Thu",
      },
    ],
  },
  {
    groupName: "Chấm ngày - Bán trú",
    items: [
      {
        reportType: "ATTENDANCE_REPORT",
        name: "Báo cáo Chấm ngày",
      },
      {
        reportType: "MEAL_SETTLEMENT",
        name: "Quyết toán Suất ăn",
      },
      {
        reportType: "CARRY_FORWARD_REPORT",
        name: "Kết chuyển Dư Thừa / Thiếu",
      },
    ],
  },
  {
    groupName: "Hóa đơn - Học vụ",
    items: [
      {
        reportType: "EINVOICE_CANCEL_REPORT",
        name: "Báo cáo Xuất Hóa đơn ĐT",
      },
      {
        reportType: "STUDENT_ROSTER_REPORT",
        name: "Thống kê Sĩ số & Khối thu",
      },
    ],
  },
];

const fmtMoney = (val: any): string => {
  const num = Number(val || 0);
  if (Number.isNaN(num)) return "0 đ";
  return `${num.toLocaleString("vi-VN")} đ`;
};

const fmtNum = (val: any): string => {
  const num = Number(val || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("vi-VN");
};

export const ReportsHubPage: React.FC = () => {
  const schoolIdStr = localStorage.getItem("schoolId");
  const schoolId: number | undefined = schoolIdStr
    ? parseInt(schoolIdStr, 10)
    : undefined;

  const [activeReportType, setActiveReportType] = useState<string>(
    "CLASS_FINANCIAL_SUMMARY"
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    () => getStoredWorkMonth().month
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    () => getStoredWorkMonth().year
  );
  const [selectedClassLevel, setSelectedClassLevel] = useState<
    string | undefined
  >(undefined);
  const [selectedClassYearId, setSelectedClassYearId] = useState<
    number | undefined
  >(undefined);
  const [selectedRevenueBlockId, setSelectedRevenueBlockId] = useState<
    number | undefined
  >(undefined);
  const [selectedIncomeSchoolId, setSelectedIncomeSchoolId] = useState<
    number | undefined
  >(undefined);
  const [selectedPaymentChannel, setSelectedPaymentChannel] =
    useState<string>("ALL");
  const [viewMode, setViewMode] = useState<string>("SUMMARY");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [classList, setClassList] = useState<any[]>([]);
  const [revenueBlocks, setRevenueBlocks] = useState<any[]>([]);
  const [incomeSchools, setIncomeSchools] = useState<any[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);
  const [reportData, setReportData] = useState<SchoolReportResponse | null>(
    null
  );
  const [previewPdfOpen, setPreviewPdfOpen] = useState<boolean>(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    "Tài chính - Công nợ": true,
    "Chấm ngày - Bán trú": true,
    "Hóa đơn - Học vụ": true,
  });

  const toggleGroup = (groupName: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const activeMenuName = useMemo(() => {
    for (const g of REPORT_MENU_GROUPS) {
      const found = g.items.find((i) => i.reportType === activeReportType);
      if (found) return found.name;
    }
    return "Báo cáo";
  }, [activeReportType]);

  // Sync with global work month changes
  useEffect(() => {
    const handleWorkMonthChange = (e: any) => {
      const { month, year } = e.detail || getStoredWorkMonth();
      setSelectedMonth(month);
      setSelectedYear(year);
    };
    window.addEventListener("workMonthChanged", handleWorkMonthChange);
    return () =>
      window.removeEventListener("workMonthChanged", handleWorkMonthChange);
  }, []);

  // Load filter options (Classes, Revenue Blocks, Income Schools)
  useEffect(() => {
    if (!schoolId) return;
    getRequest(
      `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&page=1&perPage=300&sort=asc&sortBy=className`
    )
      .then((res: any) => {
        if (res?.code === 200 && res.info?.list) {
          setClassList(res.info.list);
        }
      })
      .catch(() => {});

    getRequest(`/cms/accountant/revenue-blocks?schoolId=${schoolId}`)
      .then((res: any) => {
        if (res?.code === 200 && Array.isArray(res.info)) {
          setRevenueBlocks(res.info);
        }
      })
      .catch(() => {});

    getRequest(
      `/cms/accountant/income-school/get-income-school-by-school-and-type?schoolId=${schoolId}`
    )
      .then((res: any) => {
        if (res?.code === 200 && Array.isArray(res.info)) {
          setIncomeSchools(res.info);
        }
      })
      .catch(() => {});
  }, [schoolId]);

  const filteredClassList = useMemo(() => {
    const expectedSyNorm = (
      selectedMonth >= 8
        ? `${selectedYear}-${selectedYear + 1}`
        : `${selectedYear - 1}-${selectedYear}`
    ).toLowerCase();
    const normalizeSy = (sy: any) =>
      String(sy || "")
        .toLowerCase()
        .replace(/năm học/g, "")
        .replace(/\s+/g, "");

    const matchedBySy = classList.filter(
      (c) => normalizeSy(c.schoolYear) === expectedSyNorm
    );
    const baseList = matchedBySy.length > 0 ? matchedBySy : classList;

    if (!selectedClassLevel) return baseList;
    return baseList.filter(
      (c) => String(c.classLevel || "").trim() === selectedClassLevel.trim()
    );
  }, [classList, selectedClassLevel, selectedMonth, selectedYear]);

  const filteredIncomeSchools = useMemo(() => {
    if (activeReportType === "EINVOICE_CANCEL_REPORT") {
      return incomeSchools.filter((inc: any) =>
        Boolean(inc.isEinvoice ?? inc.einvoice)
      );
    }
    if (activeReportType === "MEAL_SETTLEMENT") {
      return incomeSchools.filter((inc: any) => Number(inc.collectBy) === 3);
    }
    return incomeSchools;
  }, [incomeSchools, activeReportType]);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (schoolId) params.set("schoolId", String(schoolId));
    params.set("reportType", activeReportType);
    params.set("month", String(selectedMonth));
    params.set("year", String(selectedYear));
    if (selectedClassLevel) params.set("classLevel", selectedClassLevel);
    if (selectedClassYearId)
      params.set("classYearId", String(selectedClassYearId));
    if (
      selectedRevenueBlockId &&
      (activeReportType === "CLASS_FINANCIAL_SUMMARY" ||
        activeReportType === "INCOME_SOURCE_SUMMARY" ||
        activeReportType === "STUDENT_DEBT_DETAIL" ||
        activeReportType === "MEAL_SETTLEMENT" ||
        activeReportType === "CARRY_FORWARD_REPORT")
    ) {
      params.set("revenueBlockId", String(selectedRevenueBlockId));
    }
    if (
      selectedIncomeSchoolId &&
      (activeReportType === "INCOME_SOURCE_SUMMARY" ||
        activeReportType === "MEAL_SETTLEMENT" ||
        activeReportType === "EINVOICE_CANCEL_REPORT")
    ) {
      params.set("incomeSchoolId", String(selectedIncomeSchoolId));
    }
    if (
      activeReportType === "CASHBOOK_TRANSACTIONS" &&
      selectedPaymentChannel &&
      selectedPaymentChannel !== "ALL"
    ) {
      params.set("paymentChannel", selectedPaymentChannel);
    }
    if (viewMode) params.set("viewMode", viewMode);
    if (
      activeReportType === "CASHBOOK_TRANSACTIONS" ||
      activeReportType === "EINVOICE_CANCEL_REPORT"
    ) {
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);
    }
    return params.toString();
  }, [
    schoolId,
    activeReportType,
    selectedMonth,
    selectedYear,
    selectedClassLevel,
    selectedClassYearId,
    selectedRevenueBlockId,
    selectedIncomeSchoolId,
    selectedPaymentChannel,
    viewMode,
    fromDate,
    toDate,
  ]);

  const latestRequestIdRef = useRef<number>(0);

  const fetchReportData = useCallback(async () => {
    if (!schoolId) return;
    const reqId = ++latestRequestIdRef.current;
    setLoading(true);
    try {
      const qs = buildQueryString();
      const res: any = await getRequest(`/cms/reports/data?${qs}`);
      if (reqId !== latestRequestIdRef.current) return;
      if (res?.code === 200 && res.info) {
        setReportData(res.info);
      } else {
        toast.error(res?.msg || "Không thể tải dữ liệu báo cáo!");
      }
    } catch {
      if (reqId !== latestRequestIdRef.current) return;
      toast.error("Lỗi kết nối khi tải dữ liệu báo cáo!");
    } finally {
      if (reqId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [schoolId, buildQueryString]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleExportExcel = async () => {
    if (!schoolId) return;
    setExportingExcel(true);
    try {
      const qs = buildQueryString();
      const fileName = `Bao_Cao_${activeReportType}_T${String(
        selectedMonth
      ).padStart(2, "0")}_${selectedYear}.xlsx`;
      await downloadRequest(`/cms/reports/export?${qs}`, fileName);
      toast.success("Xuất file Excel thành công!");
    } catch {
      toast.error("Lỗi khi xuất file Excel!");
    } finally {
      setExportingExcel(false);
    }
  };

  const handleSelectReportMenu = (item: ReportMenuItem) => {
    if (item.reportType === activeReportType) return;
    setActiveReportType(item.reportType);
    if (selectedIncomeSchoolId !== undefined) {
      const currentInc = incomeSchools.find(
        (inc: any) => inc.id === selectedIncomeSchoolId
      );
      const isEinvoiceSource = Boolean(
        currentInc?.isEinvoice ?? currentInc?.einvoice
      );
      if (
        !currentInc ||
        (item.reportType === "EINVOICE_CANCEL_REPORT" && !isEinvoiceSource) ||
        (item.reportType === "MEAL_SETTLEMENT" &&
          Number(currentInc.collectBy) !== 3) ||
        (item.reportType !== "INCOME_SOURCE_SUMMARY" &&
          item.reportType !== "MEAL_SETTLEMENT" &&
          item.reportType !== "EINVOICE_CANCEL_REPORT")
      ) {
        setSelectedIncomeSchoolId(undefined);
      }
    }
    if (
      item.reportType === "ATTENDANCE_REPORT" ||
      item.reportType === "CLASS_FINANCIAL_SUMMARY"
    ) {
      setViewMode(selectedClassYearId ? "DETAIL" : "SUMMARY");
    } else if (item.reportType === "EINVOICE_CANCEL_REPORT") {
      setViewMode("ALL");
    } else {
      setViewMode("SUMMARY");
    }
  };

  // =========================================================================
  // COMPACT 2-LINE KPI SUMMARY CARDS
  // =========================================================================

  const renderKpiCards = () => {
    const sum = reportData?.summary || {};
    switch (activeReportType) {
      case "CLASS_FINANCIAL_SUMMARY":
      case "INCOME_SOURCE_SUMMARY":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Dư đầu kỳ (Thừa / Nợ)</div>
              <div className="reports-kpi-value" style={{ fontSize: 15 }}>
                <span style={{ color: "#16a34a" }}>
                  +{fmtNum(sum.excessCarryAmount)}
                </span>{" "}
                /{" "}
                <span style={{ color: "#dc2626" }}>
                  -{fmtNum(sum.debtCarryAmount)}
                </span>
              </div>
            </div>
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Tổng phải thu</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.mustCollectAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Đã thu</div>
              <div className="reports-kpi-value" style={{ color: "#15803d" }}>
                {fmtMoney(sum.paidAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-red">
              <div className="reports-kpi-label">Còn nợ</div>
              <div className="reports-kpi-value" style={{ color: "#dc2626" }}>
                {fmtMoney(sum.remainingAmount)}
              </div>
            </div>
          </div>
        );

      case "STUDENT_DEBT_DETAIL":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card kpi-red">
              <div className="reports-kpi-label">Số HS còn nợ</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.totalDebtStudents)} HS
              </div>
            </div>
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Nợ kỳ trước</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.debtFromPrevMonth)}
              </div>
            </div>
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Phát sinh tháng</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.monthAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-red">
              <div className="reports-kpi-label">Tổng còn nợ</div>
              <div className="reports-kpi-value" style={{ color: "#dc2626" }}>
                {fmtMoney(sum.remainingDebtAmount)}
              </div>
            </div>
          </div>
        );

      case "CASHBOOK_TRANSACTIONS":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Tổng thực thu</div>
              <div className="reports-kpi-value" style={{ color: "#15803d" }}>
                {fmtMoney(sum.totalAmount)}
              </div>
            </div>
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Tiền mặt</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.cashAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-purple">
              <div className="reports-kpi-label">Chuyển khoản QR</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.vietqrAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Ví MoMo</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.momoAmount)}
              </div>
            </div>
          </div>
        );

      case "ATTENDANCE_REPORT":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Sĩ số</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.totalStudents)} HS
              </div>
            </div>
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Tổng có mặt</div>
              <div className="reports-kpi-value" style={{ color: "#15803d" }}>
                {fmtNum(sum.totalPresentUnits)} công
              </div>
            </div>
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Tổng vắng</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.totalAbsentUnits)} ngày
              </div>
            </div>
            <div className="reports-kpi-card kpi-purple">
              <div className="reports-kpi-label">Tỷ lệ chuyên cần</div>
              <div className="reports-kpi-value">
                {sum.attendanceRate || 0}%
              </div>
            </div>
          </div>
        );

      case "MEAL_SETTLEMENT":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Tiền ĐK đầu tháng</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.expectedAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Tiền đã đóng</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.paidAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-purple">
              <div className="reports-kpi-label">Tiền ăn thực tế</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.actualAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Chênh lệch (Thừa / Thiếu)</div>
              <div className="reports-kpi-value" style={{ fontSize: 15 }}>
                <span style={{ color: "#16a34a" }}>
                  +{fmtNum(sum.excessDiffAmount)}
                </span>{" "}
                /{" "}
                <span style={{ color: "#dc2626" }}>
                  -{fmtNum(sum.debtDiffAmount)}
                </span>
              </div>
            </div>
          </div>
        );

      case "CARRY_FORWARD_REPORT":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Dư đầu kỳ (Thừa / Nợ)</div>
              <div className="reports-kpi-value" style={{ fontSize: 15 }}>
                <span style={{ color: "#16a34a" }}>
                  +{fmtNum(sum.openingExcessAmount)}
                </span>{" "}
                /{" "}
                <span style={{ color: "#dc2626" }}>
                  -{fmtNum(sum.openingDebtAmount)}
                </span>
              </div>
            </div>
            <div className="reports-kpi-card kpi-purple">
              <div className="reports-kpi-label">Đã nộp trong kỳ</div>
              <div className="reports-kpi-value">
                {fmtMoney(sum.monthPaidAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Thừa chuyển kỳ sau (+)</div>
              <div className="reports-kpi-value" style={{ color: "#16a34a" }}>
                +{fmtMoney(sum.closingExcessToNext)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-red">
              <div className="reports-kpi-label">Nợ chuyển kỳ sau (-)</div>
              <div className="reports-kpi-value" style={{ color: "#dc2626" }}>
                -{fmtMoney(sum.closingDebtToNext)}
              </div>
            </div>
          </div>
        );

      case "EINVOICE_CANCEL_REPORT":
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card kpi-purple">
              <div className="reports-kpi-label">HĐĐT còn lại</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.remainingQuota)} / {fmtNum(sum.totalPurchasedQuota)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Số HĐ đã xuất</div>
              <div className="reports-kpi-value" style={{ color: "#15803d" }}>
                {fmtNum(sum.issuedEinvoiceCount)} HĐ
              </div>
            </div>
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Tiền đã xuất HĐ</div>
              <div className="reports-kpi-value" style={{ color: "#15803d" }}>
                {fmtMoney(sum.issuedEinvoiceAmount)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Chờ xuất HĐ</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.pendingEinvoiceCount)} BL
              </div>
            </div>
          </div>
        );

      case "STUDENT_ROSTER_REPORT":
      default:
        return (
          <div className="reports-kpi-grid">
            <div className="reports-kpi-card">
              <div className="reports-kpi-label">Đang học / Tổng HS</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.activeStudents)} / {fmtNum(sum.totalStudents)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-purple">
              <div className="reports-kpi-label">Nam / Nữ</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.maleStudents)} / {fmtNum(sum.femaleStudents)}
              </div>
            </div>
            <div className="reports-kpi-card kpi-green">
              <div className="reports-kpi-label">Đã gắn Khối thu</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.assignedRevenueBlockCount)} HS
              </div>
            </div>
            <div className="reports-kpi-card kpi-amber">
              <div className="reports-kpi-label">Chưa gắn Khối thu</div>
              <div className="reports-kpi-value">
                {fmtNum(sum.unassignedRevenueBlockCount)} HS
              </div>
            </div>
          </div>
        );
    }
  };

  // =========================================================================
  // CONCISE TABLE COLUMNS PER REPORT TYPE
  // =========================================================================

  const columns: ColumnsType<any> = useMemo(() => {
    switch (activeReportType) {
      case "CLASS_FINANCIAL_SUMMARY":
        if (viewMode === "DETAIL") {
          return [
            {
              title: "STT",
              dataIndex: "stt",
              width: 50,
              align: "center",
              render: (v: number, r: any) =>
                r.rowType === "CLASS_GROUP" ? "" : v,
            },
            {
              title: "Mã HS",
              dataIndex: "studentCode",
              width: 100,
              align: "center",
            },
            {
              title: "Họ tên học sinh",
              dataIndex: "studentName",
              width: 190,
              render: (v: string, r: any) => (
                <strong
                  style={
                    r.rowType === "CLASS_GROUP"
                      ? { color: "#1d4ed8" }
                      : undefined
                  }
                >
                  {v}
                </strong>
              ),
            },
            {
              title: "Lớp",
              dataIndex: "className",
              width: 85,
              align: "center",
              render: (v: string, r: any) =>
                r.rowType === "CLASS_GROUP" ? (
                  <strong style={{ color: "#1d4ed8" }}>{v}</strong>
                ) : (
                  v
                ),
            },
            {
              title: "Khối thu",
              dataIndex: "revenueBlockName",
              width: 130,
              ellipsis: true,
            },
            {
              title: "Thừa ĐK (+)",
              dataIndex: "excessCarryAmount",
              width: 120,
              align: "right",
              render: (v: number) => (
                <span style={{ color: v > 0 ? "#16a34a" : "#64748b" }}>
                  {v > 0 ? `+${fmtNum(v)}` : "0"}
                </span>
              ),
            },
            {
              title: "Nợ ĐK (-)",
              dataIndex: "debtCarryAmount",
              width: 120,
              align: "right",
              render: (v: number) => (
                <span style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                  {v > 0 ? `-${fmtNum(v)}` : "0"}
                </span>
              ),
            },
            {
              title: "Phát sinh",
              dataIndex: "monthAmount",
              width: 125,
              align: "right",
              render: (v: number, r: any) =>
                r.rowType === "CLASS_GROUP" ? (
                  <strong>{fmtNum(v)}</strong>
                ) : (
                  fmtNum(v)
                ),
            },
            {
              title: "Phải thu",
              dataIndex: "mustCollectAmount",
              width: 125,
              align: "right",
              render: (v: number) => <strong>{fmtNum(v)}</strong>,
            },
            {
              title: "Đã thu",
              dataIndex: "paidAmount",
              width: 125,
              align: "right",
              render: (v: number) => (
                <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
              ),
            },
            {
              title: "Còn nợ",
              dataIndex: "remainingAmount",
              width: 125,
              align: "right",
              render: (v: number) => (
                <strong style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                  {fmtNum(v)}
                </strong>
              ),
            },
            {
              title: "Trạng thái",
              dataIndex: "paymentStatusLabel",
              width: 125,
              align: "center",
              render: (v: string, r: any) => (
                <Tag
                  color={
                    r.remainingAmount > 0
                      ? "error"
                      : v === "Đã nộp đủ" || v === "Đã thu đủ"
                      ? "success"
                      : "default"
                  }
                >
                  {v}
                </Tag>
              ),
            },
          ];
        }
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Khối", dataIndex: "classLevel", width: 70, align: "center" },
          {
            title: "Lớp",
            dataIndex: "className",
            width: 100,
            render: (v: string) => <strong>{v}</strong>,
          },
          {
            title: "Sĩ số",
            dataIndex: "totalStudents",
            width: 75,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "HS ĐK",
            dataIndex: "registeredStudents",
            width: 75,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Thừa ĐK (+)",
            dataIndex: "excessCarryAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#16a34a" : "#64748b" }}>
                {v > 0 ? `+${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Nợ ĐK (-)",
            dataIndex: "debtCarryAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {v > 0 ? `-${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Phát sinh",
            dataIndex: "monthAmount",
            width: 125,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Phải thu",
            dataIndex: "mustCollectAmount",
            width: 125,
            align: "right",
            render: (v: number) => <strong>{fmtNum(v)}</strong>,
          },
          {
            title: "Đã thu",
            dataIndex: "paidAmount",
            width: 125,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
            ),
          },
          {
            title: "Còn nợ",
            dataIndex: "remainingAmount",
            width: 125,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {fmtNum(v)}
              </strong>
            ),
          },
          {
            title: "HS Nợ",
            dataIndex: "debtStudentsCount",
            width: 75,
            align: "right",
            render: (v: number) =>
              v > 0 ? <Tag color="error">{v}</Tag> : "0",
          },
          {
            title: "Trạng thái",
            dataIndex: "settleStatusLabel",
            width: 135,
            align: "center",
            render: (v: string, r: any) => (
              <Tag
                color={
                  r.settleStatus === 2
                    ? "purple"
                    : r.settleStatus === 1
                    ? "cyan"
                    : "default"
                }
              >
                {v}
              </Tag>
            ),
          },
        ];

      case "INCOME_SOURCE_SUMMARY":
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Mã", dataIndex: "incomeCode", width: 95, align: "center" },
          {
            title: "Nguồn thu",
            dataIndex: "incomeName",
            width: 190,
            render: (v: string) => <strong>{v}</strong>,
          },
          {
            title: "Hình thức",
            dataIndex: "collectByLabel",
            width: 140,
            align: "center",
            render: (v: string, r: any) => (
              <Tag
                color={
                  r.collectBy === 3
                    ? "processing"
                    : r.collectBy === 1
                    ? "blue"
                    : "gold"
                }
              >
                {v}
              </Tag>
            ),
          },
          {
            title: "HĐĐT",
            dataIndex: "isEinvoice",
            width: 120,
            align: "center",
            render: (_: any, r: any) => {
              const hasEinvoice = Boolean(r.isEinvoice ?? r.einvoice);
              return (
                <Tag color={hasEinvoice ? "cyan" : "default"}>
                  {hasEinvoice ? "Có xuất HĐ" : "Không HĐ"}
                </Tag>
              );
            },
          },
          {
            title: "HS ĐK",
            dataIndex: "registeredStudents",
            width: 80,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Số lượng",
            dataIndex: "expectedUnits",
            width: 90,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Đơn giá",
            dataIndex: "unitPrice",
            width: 110,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Thừa ĐK (+)",
            dataIndex: "excessCarryAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#16a34a" : "#64748b" }}>
                {v > 0 ? `+${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Nợ ĐK (-)",
            dataIndex: "debtCarryAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {v > 0 ? `-${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Phát sinh",
            dataIndex: "monthAmount",
            width: 125,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Phải thu",
            dataIndex: "mustCollectAmount",
            width: 125,
            align: "right",
            render: (v: number) => <strong>{fmtNum(v)}</strong>,
          },
          {
            title: "Đã thu",
            dataIndex: "paidAmount",
            width: 125,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
            ),
          },
          {
            title: "Còn nợ",
            dataIndex: "remainingAmount",
            width: 125,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {fmtNum(v)}
              </strong>
            ),
          },
        ];

      case "STUDENT_DEBT_DETAIL":
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Mã HS", dataIndex: "studentCode", width: 105, align: "center" },
          {
            title: "Họ tên học sinh",
            dataIndex: "studentName",
            width: 170,
            render: (v: string) => <strong>{v}</strong>,
          },
          { title: "Lớp", dataIndex: "className", width: 85, align: "center" },
          { title: "Khối thu", dataIndex: "revenueBlockName", width: 120 },
          { title: "SĐT PH", dataIndex: "parentPhone", width: 110, align: "center" },
          {
            title: "Nợ ĐK",
            dataIndex: "debtFromPrevMonth",
            width: 115,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {fmtNum(v)}
              </span>
            ),
          },
          {
            title: "Phát sinh",
            dataIndex: "monthAmount",
            width: 120,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Phải thu",
            dataIndex: "mustCollectAmount",
            width: 120,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Đã nộp",
            dataIndex: "paidAmount",
            width: 115,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Còn nợ",
            dataIndex: "remainingDebtAmount",
            width: 125,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#dc2626" }}>{fmtNum(v)}</strong>
            ),
          },
          {
            title: "Khoản nợ",
            dataIndex: "unpaidItemsSummary",
            width: 240,
            ellipsis: true,
          },
          {
            title: "Nhắc phí",
            dataIndex: "notificationCount",
            width: 85,
            align: "center",
            render: (v: number) =>
              v > 0 ? <Tag color="orange">{v}</Tag> : "0",
          },
        ];

      case "CASHBOOK_TRANSACTIONS":
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Ngày thu", dataIndex: "paymentDateStr", width: 135, align: "center" },
          { title: "Mã BL", dataIndex: "billId", width: 140, align: "center" },
          { title: "Mã GD", dataIndex: "transactionCode", width: 130, align: "center" },
          { title: "Mã HS", dataIndex: "studentCode", width: 100, align: "center" },
          {
            title: "Họ tên học sinh",
            dataIndex: "studentName",
            width: 165,
            render: (v: string) => <strong>{v}</strong>,
          },
          { title: "Lớp", dataIndex: "className", width: 80, align: "center" },
          {
            title: "Nội dung thu",
            dataIndex: "incomeItemsSummary",
            width: 220,
            ellipsis: true,
          },
          {
            title: "Kênh thu",
            dataIndex: "paymentChannelLabel",
            width: 155,
            align: "center",
            render: (v: string, r: any) => (
              <Tag
                color={
                  r.paymentChannel === "VIETQR"
                    ? "purple"
                    : r.paymentChannel === "MOMO"
                    ? "magenta"
                    : "green"
                }
              >
                {v}
              </Tag>
            ),
          },
          {
            title: "HĐĐT",
            dataIndex: "invoiceStatusLabel",
            width: 135,
            align: "center",
          },
          {
            title: "Số tiền",
            dataIndex: "amount",
            width: 130,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
            ),
          },
        ];

      case "ATTENDANCE_REPORT": {
        const daysCount = reportData?.daysInMonth || 31;
        const dayCols: ColumnsType<any> = Array.from(
          { length: daysCount },
          (_, idx) => {
            const d = idx + 1;
            return {
              title: String(d).padStart(2, "0"),
              key: `day_${d}`,
              width: 38,
              align: "center" as const,
              render: (_: any, r: any) => {
                const v = r.dayValues?.[d] ?? r.dayValues?.[String(d)] ?? 0;
                if (r.rowType === "CLASS_GROUP") {
                  return (
                    <strong style={{ color: v > 0 ? "#1d4ed8" : "#94a3b8" }}>
                      {v}
                    </strong>
                  );
                }
                if (viewMode === "DETAIL") {
                  return v > 0 ? (
                    <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span>
                  ) : (
                    <span style={{ color: "#cbd5e1" }}>-</span>
                  );
                }
                return <span style={{ fontWeight: v > 0 ? 600 : 400 }}>{v}</span>;
              },
            };
          }
        );

        const prefixCols: ColumnsType<any> =
          viewMode === "DETAIL"
            ? [
                {
                  title: "STT",
                  dataIndex: "stt",
                  width: 50,
                  align: "center",
                  fixed: "left",
                  render: (v: number, r: any) =>
                    r.rowType === "CLASS_GROUP" ? "" : v,
                },
                {
                  title: "Mã HS",
                  dataIndex: "studentCode",
                  width: 100,
                  align: "center",
                  fixed: "left",
                },
                {
                  title: "Họ tên học sinh",
                  dataIndex: "studentName",
                  width: 185,
                  fixed: "left",
                  render: (v: string, r: any) => (
                    <strong
                      style={
                        r.rowType === "CLASS_GROUP"
                          ? { color: "#1d4ed8" }
                          : undefined
                      }
                    >
                      {v}
                    </strong>
                  ),
                },
                {
                  title: "Lớp",
                  dataIndex: "className",
                  width: 80,
                  align: "center",
                  render: (v: string, r: any) =>
                    r.rowType === "CLASS_GROUP" ? (
                      <strong style={{ color: "#1d4ed8" }}>{v}</strong>
                    ) : (
                      v
                    ),
                },
              ]
            : [
                { title: "STT", dataIndex: "stt", width: 50, align: "center", fixed: "left" },
                { title: "Khối", dataIndex: "classLevel", width: 65, align: "center", fixed: "left" },
                {
                  title: "Lớp",
                  dataIndex: "className",
                  width: 100,
                  fixed: "left",
                  render: (v: string) => <strong>{v}</strong>,
                },
                { title: "Sĩ số", dataIndex: "studentCount", width: 70, align: "right" },
              ];

        return [
          ...prefixCols,
          ...dayCols,
          {
            title: "Có mặt",
            dataIndex: "totalPresentUnits",
            width: 90,
            align: "right",
            fixed: "right",
            render: (v: number, r: any) => (
              <strong
                style={{
                  color: r.rowType === "CLASS_GROUP" ? "#1d4ed8" : "#15803d",
                }}
              >
                {fmtNum(v)}
              </strong>
            ),
          },
          {
            title: "Vắng",
            dataIndex: "totalAbsentUnits",
            width: 75,
            align: "right",
            fixed: "right",
            render: (v: number, r: any) =>
              r.rowType === "CLASS_GROUP" ? (
                <strong>{fmtNum(v)}</strong>
              ) : (
                fmtNum(v)
              ),
          },
        ];
      }

      case "MEAL_SETTLEMENT":
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Lớp", dataIndex: "className", width: 80, align: "center" },
          { title: "Mã HS", dataIndex: "studentCode", width: 100, align: "center" },
          { title: "Họ tên học sinh", dataIndex: "studentName", width: 165, render: (v: string) => <strong>{v}</strong> },
          { title: "Nguồn thu", dataIndex: "incomeName", width: 155 },
          {
            title: "Đơn giá",
            dataIndex: "unitPrice",
            width: 100,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Suất ĐK",
            dataIndex: "expectedUnits",
            width: 80,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Tiền ĐK",
            dataIndex: "expectedAmount",
            width: 115,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Suất đóng",
            dataIndex: "paidUnits",
            width: 90,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Tiền đóng",
            dataIndex: "paidAmount",
            width: 115,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
            ),
          },
          {
            title: "Suất TT",
            dataIndex: "actualUnits",
            width: 85,
            align: "right",
            render: (v: number) => <strong>{fmtNum(v)}</strong>,
          },
          {
            title: "Tiền TT",
            dataIndex: "actualAmount",
            width: 115,
            align: "right",
            render: (v: number) => <strong>{fmtNum(v)}</strong>,
          },
          {
            title: "Chênh lệch",
            dataIndex: "diffAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <strong
                style={{
                  color: v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#64748b",
                }}
              >
                {v > 0 ? `+${fmtNum(v)}` : fmtNum(v)}
              </strong>
            ),
          },
          {
            title: "Trạng thái",
            dataIndex: "statusLabel",
            width: 135,
            align: "center",
            render: (v: string, r: any) => (
              <Tag
                color={
                  r.diffAmount > 0
                    ? "success"
                    : r.diffAmount < 0
                    ? "error"
                    : "default"
                }
              >
                {v}
              </Tag>
            ),
          },
        ];

      case "CARRY_FORWARD_REPORT":
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Lớp", dataIndex: "className", width: 80, align: "center" },
          { title: "Mã HS", dataIndex: "studentCode", width: 100, align: "center" },
          {
            title: "Họ tên học sinh",
            dataIndex: "studentName",
            width: 165,
            render: (v: string) => <strong>{v}</strong>,
          },
          {
            title: "Thừa ĐK (+)",
            dataIndex: "openingExcessAmount",
            width: 115,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#16a34a" : "#64748b" }}>
                {v > 0 ? `+${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Nợ ĐK (-)",
            dataIndex: "openingDebtAmount",
            width: 115,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {v > 0 ? `-${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Phải thu",
            dataIndex: "monthMustCollectAmount",
            width: 120,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Đã nộp",
            dataIndex: "monthPaidAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
            ),
          },
          {
            title: "Lệch tiền ăn",
            dataIndex: "nextMealDiffAmount",
            width: 120,
            align: "right",
            render: (v: number) => (
              <span
                style={{
                  color: v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#64748b",
                }}
              >
                {v > 0 ? `+${fmtNum(v)}` : fmtNum(v)}
              </span>
            ),
          },
          {
            title: "Nợ tháng",
            dataIndex: "nextMonthlyDebtAmount",
            width: 115,
            align: "right",
            render: (v: number) => (
              <span style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {v > 0 ? `-${fmtNum(v)}` : "0"}
              </span>
            ),
          },
          {
            title: "Thừa kỳ sau (+)",
            dataIndex: "closingExcessToNext",
            width: 135,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: v > 0 ? "#16a34a" : "#64748b" }}>
                {v > 0 ? `+${fmtNum(v)}` : "0"}
              </strong>
            ),
          },
          {
            title: "Nợ kỳ sau (-)",
            dataIndex: "closingDebtToNext",
            width: 135,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: v > 0 ? "#dc2626" : "#64748b" }}>
                {v > 0 ? `-${fmtNum(v)}` : "0"}
              </strong>
            ),
          },
          {
            title: "Chi tiết",
            dataIndex: "itemsDetailSummary",
            width: 240,
            ellipsis: true,
          },
        ];

      case "EINVOICE_CANCEL_REPORT":
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Ngày xuất", dataIndex: "actionDateStr", width: 135, align: "center" },
          { title: "Số HĐĐT", dataIndex: "invoidId", width: 110, align: "center" },
          { title: "Mã BL", dataIndex: "billId", width: 140, align: "center" },
          { title: "Mã HS", dataIndex: "studentCode", width: 100, align: "center" },
          {
            title: "Họ tên học sinh",
            dataIndex: "studentName",
            width: 165,
            render: (v: string) => <strong>{v}</strong>,
          },
          { title: "Lớp", dataIndex: "className", width: 80, align: "center" },
          {
            title: "Nguồn thu",
            dataIndex: "incomeName",
            width: 210,
            ellipsis: true,
          },
          {
            title: "Số tiền",
            dataIndex: "amount",
            width: 125,
            align: "right",
            render: (v: number) => <strong>{fmtNum(v)}</strong>,
          },
          {
            title: "Trạng thái",
            dataIndex: "invoiceStatusLabel",
            width: 140,
            align: "center",
            render: (v: string, r: any) => (
              <Tag
                color={
                  [2, 3, 5, 6, 8].includes(Number(r.invoiceStatus))
                    ? "success"
                    : "processing"
                }
              >
                {v}
              </Tag>
            ),
          },
        ];

      case "STUDENT_ROSTER_REPORT":
      default:
        return [
          { title: "STT", dataIndex: "stt", width: 50, align: "center" },
          { title: "Khối", dataIndex: "classLevel", width: 70, align: "center" },
          {
            title: "Lớp",
            dataIndex: "className",
            width: 105,
            render: (v: string) => <strong>{v}</strong>,
          },
          { title: "Niên khóa", dataIndex: "schoolYear", width: 115, align: "center" },
          {
            title: "Tổng HS",
            dataIndex: "totalStudents",
            width: 85,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Đang học",
            dataIndex: "activeStudents",
            width: 85,
            align: "right",
            render: (v: number) => (
              <strong style={{ color: "#15803d" }}>{fmtNum(v)}</strong>
            ),
          },
          {
            title: "Nghỉ",
            dataIndex: "inactiveStudents",
            width: 75,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          { title: "Nam", dataIndex: "maleStudents", width: 70, align: "right" },
          { title: "Nữ", dataIndex: "femaleStudents", width: 70, align: "right" },
          {
            title: "Đã gắn KT",
            dataIndex: "assignedRevenueBlockCount",
            width: 95,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
          {
            title: "Chưa gắn KT",
            dataIndex: "unassignedRevenueBlockCount",
            width: 105,
            align: "right",
            render: (v: number) =>
              v > 0 ? <Tag color="warning">{v}</Tag> : "0",
          },
          {
            title: "Phân bổ Khối thu",
            dataIndex: "revenueBlockBreakdown",
            width: 240,
            ellipsis: true,
          },
          {
            title: "Chấm công",
            dataIndex: "attendanceConfiguredCount",
            width: 100,
            align: "right",
            render: (v: number) => fmtNum(v),
          },
        ];
    }
  }, [activeReportType, reportData?.daysInMonth, viewMode]);

  return (
    <div className="reports-hub-container">
      {/* Left Sidebar: Basic Flat Report List */}
      <div className="reports-sidebar-card">
        <div className="reports-sidebar-header">
          <h3>Danh mục báo cáo</h3>
        </div>

        <div className="reports-sidebar-groups">
          {REPORT_MENU_GROUPS.map((group) => {
            const isOpen = openGroups[group.groupName] !== false;
            return (
              <div key={group.groupName} className="reports-group-section">
                <div
                  className="reports-group-header"
                  onClick={() => toggleGroup(group.groupName)}
                >
                  <span className="reports-group-title">{group.groupName}</span>
                  <span className="reports-group-arrow">
                    {isOpen ? <DownOutlined /> : <RightOutlined />}
                  </span>
                </div>

                {isOpen && (
                  <div className="reports-group-items">
                    {group.items.map((item) => {
                      const isActive = item.reportType === activeReportType;
                      return (
                        <div
                          key={item.reportType}
                          className={`reports-menu-item ${
                            isActive ? "active" : ""
                          }`}
                          onClick={() => handleSelectReportMenu(item)}
                        >
                          <div className="reports-menu-name">{item.name}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Main Panel */}
      <div className="reports-main-panel">
        {/* Top Bar: Report Name + Period Tag + Export Buttons */}
        <div className="reports-top-bar">
          <div className="reports-title-block">
            <h2>{activeMenuName}</h2>
            <Tag color="blue" style={{ fontWeight: 600, margin: 0 }}>
              Tháng {selectedMonth}/{selectedYear}
            </Tag>
          </div>

          <div className="reports-action-buttons">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchReportData}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => setPreviewPdfOpen(true)}
              disabled={!reportData}
              style={{
                borderColor: "#dc2626",
                color: "#dc2626",
                fontWeight: 600,
              }}
            >
              Xem PDF
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={exportingExcel}
              onClick={handleExportExcel}
              style={{
                background: "#15803d",
                borderColor: "#15803d",
                fontWeight: 600,
              }}
            >
              Xuất Excel
            </Button>
          </div>
        </div>

        {/* Compact Filter Bar */}
        <div className="reports-filter-card">
          <Space wrap size={[10, 8]}>
            <Select
              value={selectedMonth}
              onChange={(v) => setSelectedMonth(v)}
              style={{ width: 105 }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <Option key={m} value={m}>
                  Tháng {m}
                </Option>
              ))}
            </Select>

            <DatePicker
              picker="year"
              allowClear={false}
              value={dayjs(`${selectedYear}`, "YYYY")}
              onChange={(d) => d && setSelectedYear(d.year())}
              style={{ width: 90 }}
            />

            <ClassLevelSelect
              schoolId={schoolId}
              value={selectedClassLevel}
              onChange={(lvl) => {
                setSelectedClassLevel(lvl);
                setSelectedClassYearId(undefined);
              }}
              allowClear
              placeholder="Tất cả khối"
              style={{ width: 130 }}
            />

            <Select
              allowClear
              placeholder="Tất cả lớp"
              value={selectedClassYearId}
              onChange={(v) => setSelectedClassYearId(v)}
              style={{ width: 160 }}
              showSearch
              optionFilterProp="children"
            >
              {filteredClassList.map((c: any) => (
                <Option key={c.classYearId} value={c.classYearId}>
                  {c.className} ({c.schoolYear})
                </Option>
              ))}
            </Select>

            {(activeReportType === "CLASS_FINANCIAL_SUMMARY" ||
              activeReportType === "INCOME_SOURCE_SUMMARY" ||
              activeReportType === "STUDENT_DEBT_DETAIL" ||
              activeReportType === "MEAL_SETTLEMENT" ||
              activeReportType === "CARRY_FORWARD_REPORT") && (
              <Select
                allowClear
                placeholder="Khối thu"
                value={selectedRevenueBlockId}
                onChange={(v) => setSelectedRevenueBlockId(v)}
                style={{ width: 150 }}
              >
                {revenueBlocks.map((rb: any) => (
                  <Option key={rb.id} value={rb.id}>
                    {rb.name}
                  </Option>
                ))}
              </Select>
            )}

            {(activeReportType === "INCOME_SOURCE_SUMMARY" ||
              activeReportType === "MEAL_SETTLEMENT" ||
              activeReportType === "EINVOICE_CANCEL_REPORT") && (
              <Select
                allowClear
                placeholder="Nguồn thu"
                value={selectedIncomeSchoolId}
                onChange={(v) => setSelectedIncomeSchoolId(v)}
                style={{ width: 220 }}
                showSearch
                optionFilterProp="children"
              >
                {filteredIncomeSchools.map((inc: any) => {
                  const baseLabel = inc.code
                    ? `${inc.code} - ${inc.name}`
                    : inc.name;
                  const hasEinvoice = Boolean(inc.isEinvoice ?? inc.einvoice);
                  const suffix =
                    activeReportType === "INCOME_SOURCE_SUMMARY"
                      ? hasEinvoice
                        ? " (Có HĐ)"
                        : " (Không HĐ)"
                      : "";
                  return (
                    <Option key={inc.id} value={inc.id}>
                      {baseLabel}
                      {suffix}
                    </Option>
                  );
                })}
              </Select>
            )}

            {activeReportType === "CASHBOOK_TRANSACTIONS" && (
              <Select
                value={selectedPaymentChannel}
                onChange={(v) => setSelectedPaymentChannel(v)}
                style={{ width: 160 }}
              >
                <Option value="ALL">Tất cả kênh thu</Option>
                <Option value="CASH">Tiền mặt</Option>
                <Option value="VIETQR">Chuyển khoản QR</Option>
                <Option value="MOMO">Ví MoMo</Option>
              </Select>
            )}

            {(activeReportType === "CASHBOOK_TRANSACTIONS" ||
              activeReportType === "EINVOICE_CANCEL_REPORT") && (
              <DatePicker.RangePicker
                placeholder={["Từ ngày", "Đến ngày"]}
                value={
                  fromDate && toDate
                    ? [dayjs(fromDate, "YYYY-MM-DD"), dayjs(toDate, "YYYY-MM-DD")]
                    : null
                }
                onChange={(dates) => {
                  setFromDate(
                    dates?.[0] ? dates[0].format("YYYY-MM-DD") : ""
                  );
                  setToDate(
                    dates?.[1] ? dates[1].format("YYYY-MM-DD") : ""
                  );
                }}
                style={{ width: 220 }}
              />
            )}

            {(activeReportType === "CLASS_FINANCIAL_SUMMARY" ||
              activeReportType === "ATTENDANCE_REPORT") && (
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="SUMMARY">Theo Lớp</Radio.Button>
                <Radio.Button value="DETAIL">Từng HS</Radio.Button>
              </Radio.Group>
            )}

            {activeReportType === "EINVOICE_CANCEL_REPORT" && (
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="ALL">Tất cả</Radio.Button>
                <Radio.Button value="ISSUED">Đã xuất HĐ</Radio.Button>
                <Radio.Button value="PENDING">Chờ xuất</Radio.Button>
              </Radio.Group>
            )}
          </Space>
        </div>

        {/* 4 Compact KPI Summary Cards */}
        {renderKpiCards()}

        {/* Main Data Table */}
        <div className="reports-table-card">
          <Spin spinning={loading}>
            <Table
              rowKey={(r: any) =>
                `${r.rowType || "ROW"}_${r.stt}_${r.classYearId || ""}_${
                  r.studentClassYearId || ""
                }_${r.incomeSchoolId || ""}_${r.billId || ""}`
              }
              rowClassName={(r: any) =>
                r.rowType === "CLASS_GROUP" ? "reports-class-group-row" : ""
              }
              columns={columns}
              dataSource={reportData?.rows || []}
              size="small"
              bordered
              scroll={{ x: "max-content", y: 540 }}
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100", "200"],
                showTotal: (total) => `Tổng ${total} dòng`,
              }}
              locale={{
                emptyText: <Empty description="Không có dữ liệu" />,
              }}
            />
          </Spin>
        </div>
      </div>

      {/* A4 Print Preview & Save-as-PDF Modal */}
      <ReportPrintPreviewModal
        open={previewPdfOpen}
        onCancel={() => setPreviewPdfOpen(false)}
        reportData={reportData}
      />
    </div>
  );
};

export default ReportsHubPage;
